/**
 * Prediction and correlation for the Readings tab over the analysis module
 * (computeComponentPrediction, computeLivePrediction, getCorrelationInfo,
 * generateCorrelationSeries, generateComparisonData). The panels work with
 * chronological numeric points; the analysis functions take the shipped
 * newest-first history, so the series is converted here.
 *
 * The analysis module fits the component-mode trend oldest to newest (the
 * previous visualizer.js fitted newest first and read "Rising" for a falling
 * reading; fixed at the source, see analysis/PORT_NOTES.md), so the direction
 * is used as is, like the health, days to failure and confidence.
 */
import {
  computeComponentPrediction,
  computeLivePrediction,
  generateComparisonData,
  generateCorrelationSeries,
  getCorrelationInfo,
  type CorrelationInfo,
  type SensorProfile,
} from '@/features/twin/analysis';
import type { HistoryPoint, NormalRange } from '../../types';
import type { ReadingRef } from '../state';
import { parseDateMs } from '../lib/format';
import type { SeriesPoint } from '../lib/graphSeries';
import { latestMetric, liveLimitsFor } from './telemetry';

export type Severity = 'critical' | 'warning' | 'ok';
export type { CorrelationInfo, SensorProfile };

export interface ComponentPrediction {
  mode: 'component';
  health: number;
  daysToFailure: number;
  failureDateMs: number;
  severity: Severity;
  confidence: number;
  trend: { direction: 'Rising' | 'Declining' | 'Stable'; rate: number; unit: string; text: string };
  recommendedAction: string;
  profile: SensorProfile;
}

export interface LivePrediction {
  mode: 'live';
  /** In-band centredness (bidirectional) or headroom to the limit, 0 to 100. */
  margin: number;
  forecastText: string;
  severity: Severity;
  confidence: number;
  trendText: string;
  recommendedAction: string;
  daysToLimit: number | null;
  target: number | null;
  perDay: number;
  limits: { hi: number; lo: number; bidir: boolean; unit: string; what: string };
}

export interface RawPoint extends SeriesPoint {
  /** The value as shipped ("1,700 RPM"); the number is used when absent. */
  raw?: string;
}

/** Chronological points to the shipped newest-first history shape. */
export function toHistory(points: RawPoint[]): HistoryPoint[] {
  return points
    .slice()
    .reverse()
    .map((p) => ({ datetime: new Date(p.t).toISOString(), value: p.raw ?? String(p.v) }));
}

export function toSeverity(s: 'critical' | 'warning' | ''): Severity {
  return s === '' ? 'ok' : s;
}

/** Component-mode prediction from chronological points; null with fewer than two numeric values. */
export function predictComponent(
  points: RawPoint[],
  normalRange: NormalRange | null | undefined,
  dataId: string,
  subsystemId: string,
  nowMs: number,
): ComponentPrediction | null {
  const r = computeComponentPrediction({
    history: toHistory(points),
    dataId,
    subsystemId,
    normalRange: normalRange ?? null,
    nowMs,
  });
  if (r.kind === 'insufficient') return null;
  // The analysis module fits the history oldest to newest, so its direction is chronological.
  const direction = r.trendDirection;
  const unit = r.profile.unit;
  return {
    mode: 'component',
    health: r.health,
    daysToFailure: r.daysUntilFailure,
    failureDateMs: r.failureDateMs,
    severity: toSeverity(r.severity),
    confidence: r.confidence,
    trend: {
      direction,
      rate: Number(r.trendRate),
      unit,
      text: `${direction} (${r.trendRate} ${unit}/reading)`,
    },
    recommendedAction: r.recommendedAction,
    profile: r.profile,
  };
}

/** Threshold-crossing forecast for a live reading; `latest` supplies the sensor's own limits when the feed reports them. */
export function predictLive(
  points: RawPoint[],
  ref: ReadingRef,
  nowMs: number,
  latest: (metric: string) => number | null = latestMetric,
): LivePrediction | null {
  const r = computeLivePrediction({
    history: toHistory(points),
    subsystemId: ref.subsystemId,
    dataId: ref.dataId,
    latest,
    nowMs,
  });
  if (r.kind === 'insufficient') return null;
  const cfg = liveLimitsFor(ref);
  return {
    mode: 'live',
    margin: r.margin,
    forecastText: r.forecastText,
    severity: toSeverity(r.severity),
    confidence: r.confidence,
    trendText: r.trendText,
    recommendedAction: r.recommendedAction,
    daysToLimit: r.daysUntilCrossing,
    target: r.target,
    perDay: r.perDay,
    limits: { hi: r.upper, lo: r.lower, bidir: cfg.bidir, unit: cfg.unit, what: cfg.what },
  };
}

export function correlationInfo(dataId: string, subsystemId: string): CorrelationInfo {
  return getCorrelationInfo(dataId, subsystemId);
}

export interface CorrelationSeries {
  lead: number[];
  lag: number[];
  leadTimeDays: number;
  days: number;
  markerDay: number;
}

/** The synthetic 180-day lead and lag traces, seeded by the ids. */
export function correlationSeries(dataId: string, subsystemId: string): CorrelationSeries {
  const s = generateCorrelationSeries(dataId, subsystemId);
  return {
    lead: s.leadData,
    lag: s.lagData,
    leadTimeDays: s.leadTimeDays,
    days: s.days,
    markerDay: s.peakDay,
  };
}

export interface AccuracyRow {
  event: string;
  predictedMs: number;
  actualMs: number;
  varianceDays: number;
}

/** Four seeded historical events with the actual date within ±25 days of the predicted one. */
export function accuracyTable(dataId: string, subsystemId: string): AccuracyRow[] {
  return generateComparisonData(dataId, subsystemId).map((r) => ({
    event: r.event,
    predictedMs: parseDateMs(r.predicted) ?? 0,
    actualMs: parseDateMs(r.actual) ?? 0,
    varianceDays: r.varianceDays,
  }));
}
